/**
 * Single source of truth for the admin navigation set.
 *
 * Both AdminSidebar.tsx and the /admin/dashboard quick-link grid
 * (Dashboard.tsx) import from here so labels, routes, icons, and module gates
 * can never drift between the two surfaces (#872, sub-issue (C) of EPIC #869).
 *
 * Each NavItem carries an optional `module` gate; consumers must hide the item
 * when `useModules().isEnabled(item.module)` is false (D4-PKG-003 — mirrors the
 * sidebar's gating exactly).
 */
import {
  BarChart3,
  Inbox,
  Landmark,
  Handshake,
  Wallet,
  DollarSign,
  CalendarClock,
  CalendarDays,
  Target,
  PieChart,
  Trophy,
  Activity,
  Gauge,
  LayoutDashboard,
  LineChart,
  Users,
  UserPlus,
  Shield,
  ShieldCheck,
  FileText,
  FileWarning,
  Plug,
  PlugZap,
  Briefcase,
  ClipboardList,
  ListChecks,
  MessageSquare,
  MessageCircleQuestion,
  Settings,
  Award,
  SlidersHorizontal,
  Receipt,
  ReceiptText,
  Hourglass,
  Archive,
  GraduationCap,
  CopyCheck,
  MapPin,
  Upload,
  Lock,
  Percent,
  KeyRound,
} from "lucide-react";
import type { SidebarNavItem } from "./NavGroupSection";
import type { ModuleKey } from "../../contexts/modules";

export interface NavItem extends SidebarNavItem {
  /** When set, the item only renders if this module is enabled (D4-PKG-003). */
  module?: ModuleKey;
  /** When true, the item only renders for a Django superuser (#1932). */
  superuserOnly?: boolean;
  /** When true, the item only renders for a user with view_commission (#2201). */
  requiresCommission?: boolean;
  /**
   * When true, the item only renders for hierarchy rank 1 (super-user) --
   * D8-ANA-W3-005 (#3038). Every admin-shell user is rank 1 by construction
   * today (back-office presets are provisioned on role="agent", never
   * role="admin" -- `backend/apps/users/hierarchy.py::rank_for`), so this is
   * belt-and-suspenders explicit gating, not a widening of who currently
   * reaches the admin shell.
   */
  rank1Only?: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
}

// Grouped, scannable nav for non-technical admins. Per #540 there is no
// standalone "Setup" item — Setup lives under My Business inside Settings.
export const adminNavGroups: NavGroup[] = [
  {
    id: "dashboards",
    label: "Dashboards",
    icon: BarChart3,
    items: [
      { to: "/admin/dashboard", label: "Overview", purpose: "Company-wide sales and pipeline at a glance", icon: LayoutDashboard, exact: true },
      { to: "/admin/monthly-deals", label: "Monthly Summary", purpose: "Deals and commission for one month", icon: CalendarDays },
      // #4620: the searchable deals list (File No., address, buyer, seller) --
      // the admin-layout mount of the shared /crm/deals page (the /admin/leases
      // pattern), so the super-user finds any deal without leaving the panel.
      { to: "/admin/deals", label: "Deals", purpose: "Find any deal by File No., address, buyer or seller", icon: FileText, exact: true },
      { to: "/admin/deal-pipeline", label: "Deal Pipeline", purpose: "Every open deal by stage", icon: BarChart3 },
      { to: "/admin/pipeline-cashflow", label: "Pipeline / Cashflow", purpose: "Expected commission cash by month", icon: Wallet },
      { to: "/admin/attorney-pipeline", label: "Attorney Pipeline", purpose: "Deals by conveyancing attorney and stage", icon: Landmark },
      // D9-EXT-009: partner directory -- firms deals are shared with.
      { to: "/admin/partners", label: "Partners", purpose: "Partner firms deals are shared with", icon: Handshake, module: "client_portal" },
      { to: "/admin/commission", label: "Commission", purpose: "Commission earned, paid and owed per agent", icon: DollarSign, module: "financial" },
      // #2201: Finance/Super-User monthly commission sheet (splits + referral +
      // company net + received status + deductions). API gated finance/admin
      // (process_payment, #2385); nav visibility stays requiresCommission.
      {
        to: "/admin/commission-sheet",
        label: "Commission Sheet",
        purpose: "Monthly commission workbook and export",
        icon: ReceiptText,
        module: "financial",
        requiresCommission: true,
      },
      // #3820: the super-user's commission sign-off inbox sits next to the
      // Commission Sheet (moved out of Compliance so it is found).
      {
        to: "/admin/commission-authorizations",
        label: "Authorizations",
        purpose: "Commission split sign-offs",
        icon: ShieldCheck,
        module: "financial",
      },
      // #2463: Outstanding Deals register -- the whole open book of
      // unregistered deals (not date-windowed) with commission-outstanding
      // totals; the in-app replacement for the OUTSTANDING DEALS workbook.
      {
        to: "/admin/outstanding-deals",
        label: "Outstanding Deals",
        purpose: "Unregistered deals and commission still due",
        icon: Hourglass,
        module: "financial",
        requiresCommission: true,
      },
      // #3720 (D2-OTP-026): Leases register -- every live lease + its
      // renewal lifecycle (due for renewal / renewed / closed).
      { to: "/admin/leases", label: "Leases", purpose: "Leases and when they are due for renewal", icon: KeyRound },
      { to: "/admin/leaderboard", label: "Leaderboard", purpose: "Agents ranked by sales", icon: Trophy, module: "financial" },
      { to: "/admin/sales-targets", label: "Sales Targets", purpose: "Set and track agent sales targets", icon: Target },
      { to: "/admin/market-share", label: "Market Share", purpose: "Our share of sales per area", icon: PieChart },
      // D5-RPT-001 (#4531): days from listing to registration per registered sale.
      { to: "/admin/listing-to-registration", label: "Listing to Registration", purpose: "Days from listing to registration per registered sale", icon: CalendarClock },
      // User Management lives under Dashboards per admin feedback (#1105).
      { to: "/admin/users", label: "User Management", purpose: "Invite, edit and deactivate users", icon: Users },
      // Super-User-authorization queue — maker-checker sign-off for manager-
      // initiated admin creation + sales-target edits (#2271 #2250, EPIC #2317).
      {
        to: "/admin/privileged-authorizations",
        label: "Authorization Requests",
        purpose: "Approve requests that need a super-user",
        icon: ShieldCheck,
      },
      // Unmatched PROP Data leads -- the enquiries the nightly import refused
      // to attach to a contact because more than one matched (#3475).
      { to: "/admin/leads/ambiguous", label: "Unmatched Leads", purpose: "Leads that matched more than one contact", icon: Inbox },
      // Buyer enquiries that arrived with no agent on them, so they are on
      // nobody's list until the office hands them out (#3474).
      { to: "/admin/leads/unassigned", label: "Unassigned Leads", purpose: "Leads waiting for an agent", icon: UserPlus },
      // Contact duplicate review queue + merge (#1439, EPIC #608).
      { to: "/admin/contacts/dedup", label: "Duplicate Contacts", purpose: "Review and merge duplicate contacts", icon: CopyCheck },
      // Deal duplicate review queue + merge (#2173).
      { to: "/admin/deals/dedup", label: "Duplicate Deals", purpose: "Review and merge duplicate deals", icon: CopyCheck },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: ClipboardList,
    items: [
      // KPI Reports — company-wide agent KPI performance (#849).
      { to: "/admin/kpis", label: "KPI Reports", purpose: "Monthly KPI results per agent", icon: Target },
      // KPI Monthly Scorecard & outputs — scorecard, Involved-vs-Share, property
      // detail, ranking, rolling average, Excel/PDF export, locked archive (#1788).
      { to: "/admin/kpi-scorecard", label: "KPI Scorecard", purpose: "KPI scores against targets", icon: BarChart3 },
      { to: "/admin/activity", label: "Activity Metrics", purpose: "Agent activity counts over time", icon: Activity },
      { to: "/admin/agent-usage", label: "Agent Usage", purpose: "How often each agent uses the CRM", icon: Gauge },
      // Usage Analytics -- rank-1 (super-admin) only adoption/friction/AI-usage
      // dashboard (D8-ANA-W3-*, #3038, epic #3034 WP3).
      {
        to: "/admin/analytics",
        label: "Usage Analytics",
        purpose: "Usage of CRM pages and features",
        icon: LineChart,
        rank1Only: true,
      },
      // Bond analytics — bonds-by-granting-bank monthly + Ooba-originator share (#2127).
      { to: "/admin/bond-analytics", label: "Bonds by Bank", purpose: "Bond approvals by bank", icon: Landmark },
      // Communications — company-wide message history + delivery health (#748).
      { to: "/admin/communications", label: "Communications", purpose: "Messages sent and their delivery", icon: MessageSquare },
      // Feedback reports — running list of in-app submissions (#547, D4-FBK-017).
      { to: "/admin/feedback/reports", label: "Feedback reports", purpose: "All problem reports and suggestions", icon: ListChecks },
      // Support insights -- how people use the in-app help assistant (D10-OPS-001, #3607).
      { to: "/admin/support-insights", label: "Support insights", purpose: "Questions asked of the help assistant and what it could not answer", icon: MessageCircleQuestion },
      // Payslips — admin/accounts on-demand commission payslip generation (#984).
      { to: "/admin/payslips", label: "Payslips", purpose: "Agent commission payslips", icon: Receipt, module: "financial" },
    ],
  },
  {
    // Documents — admin-curated company document library. The Document Vault
    // is the company / general documents store (OTP template, mandate,
    // disclosure); record-less uploads to it are admin-only and server-enforced
    // (D2-DOC-010), so the admin is its primary curator. The link targets the
    // admin-layout mount /admin/document-vault — the SAME shared component, but
    // kept inside AdminLayout so the admin never bounces into the agent CRM
    // shell; admins retain the company-wide upload there (role-derived).
    // Module-gated on `documents` exactly as the CRM sidebar gates it
    // (CrmSidebar.tsx).
    id: "documents",
    label: "Documents",
    icon: Archive,
    items: [
      {
        to: "/admin/document-vault",
        label: "Document Vault",
        purpose: "Every stored document, searchable",
        icon: Archive,
        module: "documents",
      },
      // #2813: restricted HR/personnel document templates -- a genuine
      // per-user grant (manage_hr_documents), not tied to the admin role
      // alone (an admin without the flag is still let in here since the
      // whole /admin/* shell is IsAdmin-gated; the CRM-shell mount at
      // /crm/hr-templates additionally flag-gates a non-admin, e.g. an
      // individually-granted paralegal).
      {
        to: "/admin/hr-templates",
        label: "Personnel Templates",
        purpose: "Restricted HR document templates",
        icon: Lock,
        module: "documents",
      },
    ],
  },
  {
    id: "compliance",
    label: "Compliance",
    icon: Shield,
    items: [
      { to: "/admin/compliance", label: "FICA Compliance", purpose: "FICA checks for deal parties", icon: Shield, module: "fica", exact: true },
      // Agent compliance & HR documents — contracts/FFC/PPRA/qualifications
      // (EPIC #2033 WP4, #2037). No module gate: a back-office/admin HR surface.
      { to: "/admin/compliance/agents", label: "Agent Compliance", purpose: "Agent compliance documents and expiry", icon: ShieldCheck },
      { to: "/admin/str-queue", label: "STR Queue", purpose: "Suspicious transaction reports to review", icon: FileWarning, module: "fica" },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    items: [
      { to: "/admin/business", label: "My Business", purpose: "Agency profile, branding and setup", icon: Briefcase },
      // Areas — self-service master list management (#1751). Lets an admin add
      // a missing covered area in-app, no code change needed.
      { to: "/admin/areas", label: "Areas", purpose: "Areas and suburbs agents work", icon: MapPin },
      // Import farm book — self-service .xlsx upload (#3442). Replaces the
      // "email the workbook to us and we'll run a command" round-trip.
      { to: "/admin/farm-book-import", label: "Import farm book", purpose: "Load a farm book spreadsheet", icon: Upload, module: "farm" },
      // Over-rider Schedules — self-service editing of the flat banded
      // per-deal rider. Money config, so it carries the same commission-
      // visibility gate as the Commission Sheet.
      {
        to: "/admin/overrider-schedules",
        label: "Over-rider Schedules",
        purpose: "Over-rider commission schedules",
        icon: Percent,
        module: "financial",
        requiresCommission: true,
      },
      // KPI Metrics — self-service metric catalogue editor (#1498), sits above
      // the per-tier targets at /admin/kpi-tiers.
      { to: "/admin/kpi-metrics", label: "KPI Metrics", purpose: "Choose which KPIs are tracked", icon: SlidersHorizontal },
      // KPI Monthly Upload — source-file ingestion + column mapping + staging
      // (#1784, epic #1783). Feeds the monthly scoring run.
      { to: "/admin/kpi-upload", label: "KPI Monthly Upload", purpose: "Upload monthly KPI figures", icon: Upload },
      // KPI Exceptions / Data Issues — pre-lock review gate (#1787, epic #1783).
      { to: "/admin/kpi-exceptions", label: "KPI Exceptions", purpose: "KPI figures that need a check", icon: ClipboardList },
      { to: "/admin/kpi-tiers", label: "KPI Tiers", purpose: "KPI targets per tier and agent", icon: Award },
      // Mandate/FICA checklist catalogue — admin self-service (#1741).
      { to: "/admin/checklist-templates", label: "Mandate Checklist", purpose: "Mandate checklist items", icon: ListChecks },
      // Training -- relaunch the Training Vault + guided tours inside the admin
      // shell (#1328, D4-ONB-009). Points at the /admin/training admin-layout
      // mount of the shared component (the #1153 /admin/document-vault pattern).
      { to: "/admin/training", label: "Training", purpose: "Guided tours and training material", icon: GraduationCap, module: "training" },
      { to: "/admin/connect", label: "Connect your AI", purpose: "Connect an AI assistant to the CRM", icon: Plug },
      // Integrations -- admin BYOK third-party API config, encrypted at rest
      // (#1932). Sudonum first. Gated to the admin role (#1941): server-enforced
      // on every endpoint, and the item lives in the admin shell so a plain
      // agent never sees it.
      {
        to: "/admin/integrations",
        label: "Integrations",
        purpose: "Turn on and set up integrations",
        icon: PlugZap,
      },
    ],
  },
];

/**
 * Flat lookup helper: returns the first NavItem whose `to` matches `path`,
 * across all groups. Used by Dashboard.tsx to derive quick-link cards from the
 * same data the sidebar renders, so labels/icons/module gates can never drift.
 */
export function findAdminNavItem(path: string): NavItem | undefined {
  for (const group of adminNavGroups) {
    const hit = group.items.find((item) => item.to === path);
    if (hit) return hit;
  }
  return undefined;
}
